import { pathOr } from 'ramda';
import { eventChannel } from 'redux-saga';
import {
  all,
  call,
  cancelled,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';

import { QUOTE_STATUS } from '@frontend/api';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import { UNASSIGNED_VALUE } from 'modules/common/smartComponents/TeamMemberSelect';

import processesService from 'services/processesService';
import quoteService from 'services/quoteService';
import {
  transformArrayToFilters,
  transformDateRangeToFilters,
} from 'services/transformations';
import socketSingleton from 'services/webSocketService';

import { PAGE_REQUEST } from 'store/app/actions';
import { getFeatureFlagValue, getGroupId } from 'store/session/selectors';

import {
  fetchQuotes,
  fetchQuoteWorkflow,
  types,
  quoteSocketInitialize,
} from './actions';
import { getFilters, getPage, getPageSize } from './reducer';

export default function* root() {
  yield takeLatest(types.FETCH_QUOTES_REQUEST, watchFetchQuotes);
  yield takeLatest(types.FETCH_QUOTES_UPDATE, watchFetchQuotesUpdate);
  yield takeLatest(types.QUOTE_SOCKET_INITIALIZE_REQUEST, connectToQuoteSocket);
  yield takeLatest(types.FETCH_QUOTE_WORKFLOW_REQUEST, watchFetchQuoteWorkflow);
  yield takeLatest(types.WORKFLOWS_INIT, watchInitDashboard);
  yield takeLatest(types.WORKFLOWS_FILTER, watchFetchQuotes);
  yield takeLatest(types.WORKFLOWS_PAGINATE, watchFetchQuotes);
  yield takeLatest(types.WORKFLOWS_UPDATE, watchFetchQuotes);
}

const filterKeyMap = {
  dueDate: 'due',
  contact: 'account',
  workflowCompletionPercentage: 'workflow_completion_status',
};

const orderKeyMap = {
  dueDate: 'due',
  created: 'created_on',
  contact: 'account',
  workflowCompletionPercentage: 'workflow_completion_percentage',
  numberWithRevision: 'number',
  rfqNumber: 'rfq_number',
};

const dateRangeFilters = ['due', 'created'];

function createSocketChannel() {
  return eventChannel((emit) => {
    const unsubscribe = socketSingleton.subscribe(`quotes.created`, emit);
    return unsubscribe.unsubscribe;
  });
}

export function* connectToQuoteSocket() {
  // Send a request to initialize a chat queue and receive a socket token
  yield race({
    quoteSocket: call(watchQuoteSocketMessages),
    otherPage: take(PAGE_REQUEST),
  });
}

function* watchQuoteSocketMessages() {
  const socketChannel = yield call(createSocketChannel);
  try {
    while (true) {
      // An error from socketChannel will cause the saga to jump to the catch block
      const message = yield take(socketChannel);
      if (message) {
        yield put(fetchQuotes.update());
      }
    }
  } catch (err) {
    console.error('Received a socket error, terminating the channel');
    // If we get an error on the socket connection, or if the server
    // disconnects, we want to close the channel and terminate this saga

    // We want to open a new connection to the socket server before closing
    // this one to avoid missing any messages
    yield put(quoteSocketInitialize.request());
    // The socketChannel is still open in the catch block
    // If we want to end the socketChannel, we need to close it explicitly
    socketChannel.close();
  } finally {
    // If the saga is cancelled (likely in the event that the user leaves
    // the page), manually kill the socket connection so
    // the server can deallocate resources
    if (yield cancelled()) {
      socketChannel.close();
    }
  }
}

function* watchFetchQuotesUpdate() {
  try {
    const [groupId, page, pageSize, filters] = yield all([
      select(getGroupId),
      select(getPage),
      select(getPageSize),
      select(getFilters),
    ]);
    const params = {
      status: QUOTE_STATUS.DRAFT,
      page,
      pageSize,
    };

    const orderBy = Object.keys(filters)
      .filter((key) => filters[key].sortState.direction)
      .sort(
        (a, b) => filters[a].sortState.priority - filters[b].sortState.priority,
      )
      .map(
        (key) =>
          (filters[key].sortState.direction === 'descending' ? '-' : '') +
          (key.startsWith('quoteItemWorkflowSteps')
            ? key.split(',')[1]
            : orderKeyMap[key] || key),
      )
      .join();

    let filterParams = Object.keys(filters)
      .filter((key) => filters[key].filterState)
      .reduce(
        (obj, key) => ({
          ...obj,
          [key.startsWith('quoteItemWorkflowSteps')
            ? key.split(',')[1]
            : filterKeyMap[key] || key]: filters[key].filterState,
        }),
        {},
      );

    // replace unassigned estimator option with the unassigned estimator query param
    if (pathOr([], ['estimator'], filterParams).includes(UNASSIGNED_VALUE)) {
      filterParams = {
        ...filterParams,
        estimator: undefined,
        estimatorIsUnassigned: true,
      };
    }

    if (pathOr([], ['account'], filterParams).includes(UNASSIGNED_VALUE)) {
      filterParams = {
        ...filterParams,
        account: undefined,
        accountIsUnassigned: true,
      };
    }

    if (pathOr([], ['salesperson'], filterParams).includes(UNASSIGNED_VALUE)) {
      filterParams = {
        ...filterParams,
        salesperson: undefined,
        salespersonIsUnassigned: true,
      };
    }

    for (const key of dateRangeFilters) {
      if (filterParams[key]) {
        filterParams = {
          ...filterParams,
          ...transformDateRangeToFilters(filterParams[key], key),
        };

        delete filterParams[key];
      }
    }
    filterParams = transformArrayToFilters(filterParams);

    if (orderBy) {
      params.o = orderBy;
    }
    const response = yield call(quoteService.list, groupId, {
      ...params,
      ...filterParams,
    });
    yield put(
      fetchQuotes.success({
        quotes: response.results,
        totalCount: response.count,
      }),
    );
  } catch (error) {
    yield put(fetchQuotes.error(error));
  }
}

function* watchFetchQuotes() {
  yield call(watchFetchQuotesUpdate);
  yield put(quoteSocketInitialize.request());
}

function* watchFetchQuoteWorkflow() {
  try {
    const fetchWorkflows = yield select(
      getFeatureFlagValue,
      featureFlagKeys.isWorkflowsAvailable,
    );
    if (fetchWorkflows) {
      const workflows = yield call(processesService.workflows.list, {
        type: 'quote',
      });
      yield put(fetchQuoteWorkflow.success({ quoteWorkflow: workflows[0] }));
    } else {
      yield put(fetchQuoteWorkflow.success({}));
    }
  } catch (error) {
    yield put(fetchQuoteWorkflow.error(error));
  }
}

function* watchInitDashboard() {
  try {
    yield put(fetchQuoteWorkflow.request());
  } catch (error) {
    console.error('Error configuring workflows dashboard.', error);
  }
}
